import { apiUrl, authKey } from '../app.config';
import axios from 'axios';
import CONSTANTS from '../contants';
import { IAttessation, IUser } from '../types/interfaces';
import { IAttestationById } from '../types/api';
import userStatusMonitor from '../services/UserStatusMonitor';

const api = axios.create({
  baseURL: apiUrl,
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use(
  config => {
    const token = localStorage.getItem(CONSTANTS.token);
    if (token) {
      config.headers['Authorization'] = 'Bearer ' + token;
    }
    return config;
  },
  error => {
    Promise.reject(error);
  }
);

api.interceptors.response.use(
  response => {
    return response;
  },
  async error => {
    if (error?.response && error?.response?.status === 401) {
      const pathname = window.location.pathname;
      
      // Stop monitoring on 401 error
      userStatusMonitor.stopMonitoring();
      
      // Clear storage (this will also trigger Magic logout)
      localStorage.clear();
      sessionStorage.clear();
      
      if (pathname !== '/' && !pathname.includes('compare-doc'))
        window.location.pathname = '/';
    }
    return Promise.reject(error);
  }
);

export default api;

export const onBoarding = (token: string) =>
  api.post<{ message: string; access_token: string }>('/v2/user/onboarding', {
    token,
  });

export const createHashWithDoc = (data: FormData) =>
  api.post<{ hash: string }>('/v2/user/uploadDocToCreateHash', data, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

export const wordToPDF = (data: FormData) =>
  api.post<{
    defaultPdfURL: string;
    message: string;
    pdfURL: string;
    resultStatus: boolean;
  }>('/v2/user/convertWordToPdf', data, {
    headers: {
      'Content-Type': 'multipart/form-data',
      'x-api-key': authKey,
    },
  });

export const createHashWithText = (text: string) =>
  api.post<{ hash: string }>('/v2/user/uploadTextToCreateHash ', { text });

export const searchAttestation = (input?: string | null) => {
  const requestBody = input ? { input } : {};
  return api.post('/v2/attestation/search', requestBody);
};

export const getCurrentUser = () => api.get<{ user: IUser }>('/v2/user/');

export const getAllAttesations = () =>
  api.get<{ message: string; attestationData: IAttessation[] }>(
    '/v2/attestation/allAttestations'
  );

export const getAttestationByUid = (uid: string) =>
  api.get<IAttestationById>(`/v2/attestation/inspect/${uid}`);

export const compareDocument = (data: FormData) =>
  api.post<{
    documentComparisonResult: boolean;
    hash: string;
    message: string;
  }>('/v2/attestation/compareDocument', data, {
    headers: {
      'Content-Type': 'multipart/form-data',
      'x-api-key': authKey,
    },
  });

export const addNewAttestaion = (data: FormData) =>
  api.post<{ message: string; UID: string }>(
    '/v2/attestation/addNewAttestation',
    data,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  );

export const updateUser = (data: { firstName: string; lastName: string }) =>
  api.patch<{ message: string; user: IUser }>('/v2/user/updateUser', data);

export const getMyAttestations = () =>
  api.get<{ message: string; attestationData: IAttessation[] }>(
    '/v2/attestation/myAttestations'
  );

export const getKPMGScan = () =>
  api.get<{ message: string; attestationData: IAttessation[] }>(
    '/v2/attestation/KPMGScan'
  );

export const getAttestationViaUID = (uid: string) =>
  api.get<{ message: ''; attestationData: IAttessation }>(
    `/v2/attestation/attestationByUID/${uid}`,
    {
      headers: {
        'x-api-key': authKey,
      },
    }
  );

export const emailVerification = (email: string) =>
  api.post<{ message: string; status: boolean }>(
    '/v2/user/emailVerification',
    {
      email: email,
    },
    {
      headers: {
        'x-api-key': authKey,
      },
    }
  );

export const azureVerification = (email: string, accessToken?: string) =>
  api.post<{ message: string; status: boolean }>(
    '/v2/user/azureVerification',
    {
      email: email,
      accessToken: accessToken,
    },
    {
      headers: {
        'x-api-key': authKey,
      },
    }
  );

export const verifyWorldId = (data: {
  proof: any;
  app_id: string;
  action: string;
  signal: string;
}) =>
  api.post<{ success: boolean; detail?: string }>('/v2/user/worldIdVerify', data, {
    headers: {
      'x-api-key': authKey,
    },
  });

export const getUserDBWhitelistingAndAzureStatus = () =>
  api.get<{ 
    userDBWhitelistingStatus: boolean; 
    userAzureStatus: boolean;
    userDomainWhitelistStatus:boolean
  }>('/v2/user/userDBWhitelistingAndAzureStatus');